import React from 'react';

import { Box, Button, Typography } from '@mui/material';

import { Field, Form, FormSpy } from '@hrlogics/react-components/form';
import { SelectFieldOption } from '@hrlogics/react-helper-types';

import { CompanyType, QueryMode } from 'src/api';
import { AutocompleteField, CheckboxField, SelectField } from 'src/components/finalForm';
import { usePeoById } from 'src/hooks/peo';
import { usePeoPayrollProvidersAutocomplete } from 'src/hooks/peoPayrollProvider';

import { FormField } from './constants';
import { FormValues } from './types';
import { buildUpdateFnInput } from './utils/utils';
import { formValidationSchema } from './utils/validation';

export type PeoControlledByConfigurationModalFormProps = {
  onCloseModal: () => void;
  switchClosability: (closability: boolean) => void;
  peoId: string;
};

export const PeoControlledByConfigurationModalForm: React.FC<
  PeoControlledByConfigurationModalFormProps
> = ({ onCloseModal, switchClosability, peoId }) => {
  const { peo, updatePeo } = usePeoById(peoId);

  const peoPpAutocompleteProps = usePeoPayrollProvidersAutocomplete({
    queryFilter: searchText => ({
      filter: { displayName: { contains: searchText, mode: QueryMode.Insensitive } },
    }),
  });

  const initialValues: Partial<FormValues> = {
    controlledBy: peo?.peoPayrollProvider ? CompanyType.PeoPayrollProvider : CompanyType.Peo,
    peoPayrollProvider: peo?.peoPayrollProvider ?? null,
    keepOwnRevenueShare: peo?.keepOwnRevenueShare ?? false,
  };

  const handleSubmit = React.useCallback(
    async (formValues: FormValues) => {
      if (!peo) return;

      switchClosability(false);

      try {
        await updatePeo(buildUpdateFnInput(formValues, peo));

        switchClosability(true);
        onCloseModal();
      } catch (error) {
        console.error(error);
        switchClosability(true);
      }
    },
    [switchClosability, onCloseModal, updatePeo, peo],
  );

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      subscription={{ submitting: true, valid: true }}
      validationSchema={formValidationSchema}
    >
      {({ submitting, valid }) => (
        <React.Fragment>
          <Typography variant="body1">
            This setting defines whether the PEO operates independently or under a PEO Payroll
            Provider. If assigned, the Payroll Provider becomes the parent organization and will
            have visibility into this PEO and its employers. Under a PEO Payroll Provider, the PEO's
            own revenue share is paid only when "Keep this PEO's own revenue share" is checked. The
            PEO Payroll Provider's revenue share is paid either way.
          </Typography>

          <Box mt={2.5}>
            <Field
              name={FormField.controlledBy}
              component={SelectField}
              label="Hierarchy Placement"
              options={CONTROLLED_BY_OPTIONS}
              disabled={submitting}
              fullWidth
              required
            />
          </Box>

          <FormSpy<FormValues> subscription={{ values: true }}>
            {({ values }) => (
              <React.Fragment>
                <React.Activity
                  mode={
                    values.controlledBy === CompanyType.PeoPayrollProvider ? 'visible' : 'hidden'
                  }
                >
                  <Box mt={2}>
                    <Field
                      name={FormField.peoPayrollProvider}
                      component={AutocompleteField}
                      label="Connected PEO Payroll Provider"
                      disabled={submitting}
                      required={values.controlledBy === CompanyType.PeoPayrollProvider}
                      fullWidth
                      {...peoPpAutocompleteProps}
                    />
                  </Box>
                  <Box mt={2}>
                    <Field
                      name={FormField.keepOwnRevenueShare}
                      component={CheckboxField}
                      label="Keep this PEO's own revenue share"
                      disabled={submitting}
                    />
                  </Box>
                </React.Activity>
              </React.Fragment>
            )}
          </FormSpy>

          <Box display="flex" justifyContent="center" mt={4} gap={2}>
            <Button
              type="button"
              variant="outlined"
              color="error"
              loading={submitting}
              disabled={submitting}
              onClick={onCloseModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              loading={submitting}
              disabled={submitting || !valid}
            >
              Apply
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};

const CONTROLLED_BY_OPTIONS = [
  { label: 'Standalone PEO', value: CompanyType.Peo },
  { label: 'PEO under PEO Payroll Provider', value: CompanyType.PeoPayrollProvider },
] as const satisfies SelectFieldOption<CompanyType>[];
